import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment.prod';
import { DocumentElement } from '@models/document/document-element.model';
import { DocumentDto } from '@models/protocols/document.dto';
import fontkit from '@pdf-lib/fontkit';
import { PDFDocument, PDFFont, RotationTypes } from 'pdf-lib';
import { FontService } from './font.service';

@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  public readonly CalibriFontFamily: string = 'calibri';

  constructor(
    private http: HttpClient,
    private fontService: FontService,
  ) {}

  async writePdfFromUrl(url: string, fileConfig: DocumentElement, values: any) {
    return await this.writePdfFromBuffer(
      await fetch(url).then((res) => res.arrayBuffer()),
      fileConfig,
      values,
    );
  }

  async writePdfFromBuffer(
    arrayBuffer: ArrayBuffer,
    fileConfig: DocumentElement,
    values: any,
  ) {
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    pdfDoc.registerFontkit(fontkit);
    const pages = pdfDoc.getPages();
    let keys = Object.keys(fileConfig);

    let embeddedFonts = {};

    for (let i = 0; i < pages.length; i++) {
      let page = pages[i];
      let insertIntoPage = keys.filter(
        (elem) =>
          fileConfig[elem].page.length === 0 ||
          fileConfig[elem].page.includes(i),
      );

      for (let name of insertIntoPage) {
        let config = fileConfig[name];
        let value = values[name];
        if (!this.fontService.hasFont(config.family, config.font)) {
          await this.fontService.loadFont(config.family, config.font);
        }

        let embededFont: PDFFont;

        if (embeddedFonts[`${config.family}-${config.font}`] === undefined) {
          embeddedFonts[`${config.family}-${config.font}`] =
            await pdfDoc.embedFont(
              this.fontService.getFont(config.family, config.font),
              {
                subset: true,
              },
            );
        }

        embededFont = embeddedFonts[`${config.family}-${config.font}`];

        if (config.type === 'text') {
          let x = config.x;
          let y = config.y;

          if (config.boundaries !== undefined) {
            if (
              config.boundaries.start !== undefined &&
              config.boundaries.end !== undefined
            ) {
              let textWidth = embededFont.widthOfTextAtSize(value, config.size);
              let areaWidth = config.boundaries.end - config.boundaries.start;
              x = config.boundaries.start + areaWidth / 2 - textWidth / 2;
            }

            if (
              config.boundaries.top !== undefined &&
              config.boundaries.bottom !== undefined
            ) {
              let textHeight = embededFont.heightAtSize(config.size);
              let areaHeight = config.boundaries.bottom - config.boundaries.top;
              y = config.boundaries.top + areaHeight / 2 - textHeight / 2;
            }
          }

          page.drawText(value, {
            x,
            y,
            rotate: {
              type: RotationTypes.Degrees,
              angle: config.rotate,
            },
            font: embededFont,
            color: config.color,
            size: config.size,
          });
        } else if (config.type === 'image') {
          let image = await pdfDoc.embedPng(value);
          let imgHeight = image.height;
          let imgWidth = image.width;
          let finalHeight = 0;
          let finalWidth = 0;

          if (config.adjustBy === 'height') {
            finalHeight = config.height;
            finalWidth = (finalHeight * imgWidth) / imgHeight;
          } else {
            finalWidth = config.width;
            finalHeight = (finalWidth * imgHeight) / imgWidth;
          }

          page.drawImage(image, {
            x: config.x,
            y: config.y,
            rotate: {
              type: RotationTypes.Degrees,
              angle: config.rotate,
            },
            height: finalHeight,
            width: finalWidth,
          });
        } else if (config.type === 'rectangle') {
          page.drawRectangle({
            borderWidth: 1,
            borderColor: config.color,
            x: config.x,
            y: config.y,
            rotate: {
              type: RotationTypes.Degrees,
              angle: config.rotate,
            },
            width: config.width,
            height: config.height,
          });
        }
      }
    }

    const pdfBytes = await pdfDoc.save();
    return pdfBytes;
  }

  uploadFile(document: DocumentDto, file: ArrayBuffer) {
    let formData = new FormData();

    formData.append('file', new Blob([file]));
    for (let data of Object.keys(document)) {
      formData.append(data, document[data]);
    }
    return this.http.post<DocumentDto>(
      environment.host + '/documents/upload',
      formData,
    );
  }

  protocolDocument() {}

  getDocuments() {
    return this.http.get<DocumentDto[]>(environment.host + '/documents/list');
  }

  downloadFile(doc: DocumentDto) {
    return this.http.get(
      environment.host + '/documents/download/' + doc.docId,
      { responseType: 'blob' },
    );
  }

  createDocument(arr: DocumentDto[]) {
    return this.http.post<DocumentDto[]>(
      environment.host + '/documents/create',
      arr,
    );
  }

  modifyDocumentMetadata(arr: DocumentDto[]) {
    return this.http.post<DocumentDto[]>(
      environment.host + '/documents/modify',
      arr,
    );
  }

  removeDocument(arr: DocumentDto[]) {
    return this.http.post<DocumentDto[]>(
      environment.host + '/documents/remove',
      arr,
    );
  }
}
